import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import { common } from '@mui/material/colors';
import { BaseMultiCheckbox } from 'common/components/base-checkbox-group';
import BaseDialog from 'common/components/base-dialog';
import FormProvider from 'common/components/form-provider';
import { colors } from 'common/constants';
import React from 'react';
import { useForm, useFormContext } from 'react-hook-form';
import { createStyleSheet, handleError } from 'utils/helpers';
import {
    answerApprovalDetail,
    updateAnswerApprovalStatus,
} from 'services/answerApproval.service';
import { useAppSelector } from 'common/hook';
import { RootState } from 'plugins/store';
import { IAnswerApprovalDetail, IAnswerApproval } from 'types/dataCollect';
import { useTranslation } from 'react-i18next';
import ModalConfirm from 'common/components/modal-confirm';
import { QuestionType } from 'pages/admin/campaign-overview/constants';
import StarIcon from '@mui/icons-material/Star';
import VeryDissatisfied from 'assets/images/icons/question-type/question-emoji/Very Dissatisfied.png';
import Dissatisfied from 'assets/images/icons/question-type/question-emoji/Dissatisfied.png';
import Neutral from 'assets/images/icons/question-type/question-emoji/Neutral.png';
import Satisfied from 'assets/images/icons/question-type/question-emoji/Satisfied.png';
import VerySatisfied from 'assets/images/icons/question-type/question-emoji/Very Satisfied.png';

interface IDetailProps {
    questionType: string;
    answerOptions: any[]; // eslint-disable-line
    answer: IAnswerApprovalDetail | undefined;
    questionIndex: number;
}

const emojisForFiveStars = [
    VeryDissatisfied,
    Dissatisfied,
    Neutral,
    Satisfied,
    VerySatisfied,
];
const emojisForThreeItems = [VeryDissatisfied, Neutral, VerySatisfied];

const RenderAnswerDetail: React.FC<IDetailProps> = ({
    questionType,
    answerOptions,
    answer,
    questionIndex,
}) => {
    const methods = useFormContext();
    const renderImagesWithTitle = (
        imageOptions: { answer_option_id: number; image: string }[] | undefined
    ) => {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    alignItems: 'start',
                    justifyContent: 'start',
                }}
            >
                {(imageOptions || []).map((item, index) => (
                    <>
                        Đáp án:{' '}
                        {
                            answerOptions.find(
                                (option) => option.id === item.answer_option_id
                            )?.text
                        }
                        <img
                            key={index}
                            src={item.image}
                            alt={`Ảnh mô tả ${index + 1}`}
                            style={{
                                height: '200px',
                                borderRadius: '8px',
                                marginBottom: '10px',
                            }}
                        />
                    </>
                ))}
            </Box>
        );
    };

    const renderImages = (images: string[] | undefined) => {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    alignItems: 'start',
                    justifyContent: 'start',
                }}
            >
                {(images || []).map((item, index) => (
                    <img
                        key={index}
                        src={item}
                        alt={`Ảnh mô tả ${index + 1}`}
                        style={{
                            height: '300px',
                            borderRadius: '8px',
                            marginBottom: '10px',
                        }}
                    />
                ))}
            </Box>
        );
    };

    const renderAnswerMultiAnswerOptions = (
        matrix: { id?: number; rating?: number }[] | undefined,
        questionType: string
    ) => {
        return (
            <>
                {(matrix || []).map((item, index) => {
                    return (
                        <>
                            Đáp án:{' '}
                            {
                                answerOptions.find(
                                    (option) => option.id === item.id
                                )?.text
                            }
                            <Box
                                key={index}
                                sx={{ display: 'flex', alignItems: 'stretch' }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: '18px',
                                        fontWeight: '700',
                                    }}
                                >
                                    {item?.rating}
                                </Typography>
                                {questionType === 'score_star_half' ||
                                    (questionType === 'score_star_full' && (
                                        <Box sx={{ ml: 1 }}>
                                            <StarIcon
                                                sx={{
                                                    color: colors.main,
                                                    '&:hover': {
                                                        color: colors.main,
                                                    },
                                                }}
                                            />
                                        </Box>
                                    ))}
                            </Box>
                        </>
                    );
                })}
            </>
        );
    };
    const renderAnswerEmoji = (
        matrix: { id?: number; rating?: number }[] | undefined
    ) => {
        return (
            <>
                {(matrix || []).map((item, index) => {
                    return (
                        <>
                            Đáp án:{' '}
                            {
                                answerOptions.find(
                                    (option) => option.id === item.id
                                )?.text
                            }
                            <Box
                                mt={1}
                                key={index}
                                sx={{ display: 'flex', alignItems: 'stretch' }}
                            >
                                <img
                                    src={
                                        emojisForFiveStars[
                                            (item?.rating ?? 0) - 1
                                        ] ??
                                        emojisForThreeItems[
                                            (item?.rating ?? 0) - 1
                                        ]
                                    }
                                    alt={`Ảnh mô tả ${index + 1}`}
                                    style={{
                                        width: '32px',
                                        height: '32px',
                                        objectFit: 'cover',
                                        borderRadius: '8px',
                                        marginBottom: '10px',
                                    }}
                                />
                            </Box>
                        </>
                    );
                })}
            </>
        );
    };
    const renderAnswerMatrix = (
        matrix:
            | {
                  question?: string;
                  answer?: string[];
              }[]
            | string[][],
        questionType: string
    ) => {
        return (
            <>
                {(questionType === QuestionType.MatrixRadio ||
                    questionType === QuestionType.MatrixCheckbox) &&
                    matrix.map((item, index) => {
                        return (
                            <Box
                                mt={1}
                                key={index}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                {!Array.isArray(item) && item.question && (
                                    <Typography
                                        sx={{
                                            fontSize: '16px',
                                            fontWeight: '600',
                                        }}
                                    >
                                        {item.question}:{' '}
                                        {item.answer?.map((i) => i).join('; ')}
                                    </Typography>
                                )}
                            </Box>
                        );
                    })}
                {questionType === QuestionType.MatrixText &&
                    matrix.map((item, index) => {
                        return (
                            <Box
                                mt={1}
                                key={index}
                                sx={{
                                    display: 'flex',
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: '16px',
                                        fontWeight: '600',
                                    }}
                                >
                                    {Array.isArray(item) && item[0]}:{' '}
                                    {Array.isArray(item) &&
                                        item
                                            .slice(1)
                                            .filter(
                                                (value) =>
                                                    value && value.trim() !== ''
                                            )
                                            .join('; ')}
                                </Typography>
                            </Box>
                        );
                    })}
            </>
        );
    };

    const renderAnswerRank = (rank: number[] | undefined) => {
        return (
            <>
                {rank?.map((item, index) => {
                    return (
                        <>
                            {/* Đáp án:{' '}
                            {
                                questionDetail?.question.attributes.answer_options.find(
                                    (option) => option.id === item.id
                                )?.text
                            } */}
                            <Box
                                key={index}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: '16px',
                                        fontWeight: '700',
                                    }}
                                >
                                    {' '}
                                    Vị trí {index + 1}:
                                </Typography>

                                <Typography
                                    sx={{
                                        fontSize: '16px',
                                    }}
                                >
                                    {
                                        answerOptions.find(
                                            (option) => option.id === item
                                        )?.text
                                    }
                                </Typography>
                            </Box>
                        </>
                    );
                })}
            </>
        );
    };

    React.useEffect(() => {
        if (questionType === QuestionType.Checkbox) {
            methods.setValue(
                `answers.${questionIndex}`,
                answer?.multi_choices?.map((id) => Number(id))
            );
        }
        if (
            questionType === QuestionType.SingleChoice ||
            questionType === QuestionType.Dropdown
        ) {
            methods.setValue(`answers.${questionIndex}`, [answer?.choice]);
        }
        if (
            questionType === QuestionType.TextboxRecommend ||
            questionType === QuestionType.TextboxRecommendMultiChoice
        ) {
            methods.setValue(
                `answers.${questionIndex}`,
                answer?.text_recommend?.map((text) => text)
            );
        }
        // eslint-disable-next-line
    }, []);

    return (
        <Box mt={1}>
            {[
                QuestionType.Textbox,
                QuestionType.Date,
                QuestionType.Number,
            ].includes(questionType as QuestionType) && (
                <Box>
                    <Typography
                        sx={{
                            fontSize: '16px',
                            fontWeight: '600',
                        }}
                    >
                        Câu trả lời: {''}
                        {answer?.text && <span>{answer?.text}</span>}
                    </Typography>
                </Box>
            )}
            {[QuestionType.Score].includes(questionType as QuestionType) && (
                <Box>
                    <Typography
                        sx={{
                            fontSize: '16px',
                            fontWeight: '600',
                        }}
                    >
                        Câu trả lời: {''}
                        {answer?.rating && <span>{answer?.rating}</span>}
                    </Typography>
                </Box>
            )}
            {[
                QuestionType.Checkbox,
                QuestionType.TextboxRecommend,
                QuestionType.TextboxRecommendMultiChoice,
                QuestionType.SingleChoice,
                QuestionType.Dropdown,
            ].includes(questionType as QuestionType) && (
                <BaseMultiCheckbox
                    key={JSON.stringify(
                        methods.watch(`answers.${questionIndex}`)
                    )}
                    sxCheckbox={{
                        '& .Mui-disabled.Mui-checked .MuiSvgIcon-root': {
                            color: `${colors.main} !important`,
                        },
                        '& .MuiFormControlLabel-label': {
                            fontWeight: '500',
                        },
                        '& .Mui-checked + .MuiFormControlLabel-label': {
                            color: colors.main,
                        },
                    }}
                    disabled
                    name={`answers.${questionIndex}`}
                    options={
                        answerOptions.map((item) => {
                            return {
                                label: item?.text,
                                value: Number(item.id),
                            };
                        }) ?? []
                    }
                />
            )}
            {[QuestionType.Image].includes(questionType as QuestionType) &&
                renderImages(answer?.images)}
            {[QuestionType.ImageWithTitle].includes(
                questionType as QuestionType
            ) && renderImagesWithTitle(answer?.images_option)}
            {[
                QuestionType.ScoreStarFull,
                QuestionType.ScoreStarHalf,
                QuestionType.SliderScale,
            ].includes(questionType as QuestionType) &&
                renderAnswerMultiAnswerOptions(answer?.matrix, questionType)}
            {[QuestionType.Emoji].includes(questionType as QuestionType) &&
                renderAnswerEmoji(answer?.matrix)}
            {[
                QuestionType.MatrixRadio,
                QuestionType.MatrixCheckbox,
                QuestionType.MatrixText,
            ].includes(questionType as QuestionType) &&
                renderAnswerMatrix(answer?.matrix || [], questionType)}
            {[QuestionType.Rank].includes(questionType as QuestionType) &&
                renderAnswerRank(answer?.rank)}
        </Box>
    );
};

interface IProps {
    open: boolean;
    onClose: (open: boolean) => void;
    answerApprove: IAnswerApproval | null;
    onChangeAnswerApprovedStatus: (answerId: string, status: string) => void;
    listQuestions: any[]; // eslint-disable-line
}

const DialogAnswerModeration: React.FC<IProps> = ({
    open,
    onClose,
    answerApprove,
    onChangeAnswerApprovedStatus,
    listQuestions,
}) => {
    const { t } = useTranslation();
    const [answers, setAnswers] = React.useState<IAnswerApprovalDetail[]>([]);
    const methods = useForm({});
    const [openModalConfirm, setOpenModalConfirm] = React.useState(false);
    const [modalConfirmInfo, setModalConfirmInfo] = React.useState({
        title: '',
        content: <></>,
    });
    const [statusToUpdate, setStatusToUpdate] = React.useState('');
    const onSubmit = methods.handleSubmit((data) => {
        console.log(data);
    });
    const currentCampaign = useAppSelector(
        (state: RootState) => state.campaignState.campaign
    );
    const renderApprovalStatus = (status: string) => {
        let bgColor = '';
        let textColor = '';

        switch (status) {
            case 'Chưa duyệt':
                bgColor = '#FFEEE2';
                textColor = '#FF7916';
                break;
            case 'Đã duyệt':
                bgColor = '#EFFBFF';
                textColor = '#475EFB';
                break;
            case 'Từ chối':
                bgColor = 'var(--color-primary-light-40)';
                textColor = 'var(--color-primary)';
                break;
        }

        return (
            <Chip
                label={status}
                sx={{
                    borderRadius: 'unset',
                    fontSize: '12px',
                    backgroundColor: bgColor,
                    color: textColor,
                    border: 'unset',
                }}
            />
        );
    };

    const handleUpdateStatus = async () => {
        if (!answerApprove || !answerApprove.id) return;

        try {
            const paramsStatusToUpdate =
                statusToUpdate === t('page.dataCollect.status.approved')
                    ? 'approved'
                    : 'rejected';
            const response = await updateAnswerApprovalStatus(
                Number(currentCampaign?.id),
                Number(answerApprove?.id),
                paramsStatusToUpdate
            );
            if (response.success) {
                onChangeAnswerApprovedStatus(answerApprove?.id, statusToUpdate);
                setOpenModalConfirm(false);
                onClose(false);
            }
        } catch (error) {
            handleError(error);
        }
    };

    React.useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await answerApprovalDetail(
                    Number(currentCampaign?.id),
                    Number(answerApprove?.id)
                );
                if (response.success) {
                    setAnswers(
                        response.data.map((item: any) => item.attributes) // eslint-disable-line
                    );
                }
            } catch (error) {
                handleError(error);
            }
        };
        if (currentCampaign && answerApprove) {
            fetchData();
        }
    }, [currentCampaign, answerApprove]);

    return (
        <BaseDialog
            open={open}
            onClose={onClose}
            fullWidth={true}
            maxWidth="md"
            sxContent={{
                padding: 4,
            }}
        >
            {answerApprove === null && (
                <Typography>Đang tải dữ liệu...</Typography>
            )}
            {answerApprove !== null && (
                <FormProvider methods={methods} onSubmit={onSubmit}>
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 'bold',
                            mt: 4,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: '10px',
                        }}
                    >
                        <Box
                            sx={{
                                fontWeight: 'bold',
                                fontSize: '24px',
                                color: common.black,
                                wordBreak: 'break-word',
                            }}
                        >
                            Kiểm duyệt đáp viên &quot;{answerApprove.user_id} -{' '}
                            {answerApprove.userName}&quot;
                        </Box>
                        {renderApprovalStatus(answerApprove.status)}
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#0000004D',
                            mb: 2,
                        }}
                    >
                        Đã gửi {answerApprove.time} trên thiết bị{' '}
                        {answerApprove.device}
                    </Typography>
                    <Stack
                        spacing={2}
                        alignItems={'center'}
                        justifyContent={'space-between'}
                        flexDirection={'row'}
                        mb={2}
                    >
                        <Typography
                            variant="body1"
                            sx={{
                                fontSize: '18px',
                                fontWeight: '600',
                                color: '#000',
                                mb: 2,
                            }}
                        >
                            Tổng số phản hồi: {answerApprove.totalFeedback} câu
                            hỏi
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                fontSize: '18px',
                                fontWeight: '600',
                                color: '#000',
                                backgroundColor: '#FFEAED',
                                mb: 2,
                            }}
                        >
                            Thời gian trung bình trả lời:{' '}
                            {answerApprove.timeAvg}
                        </Typography>
                    </Stack>
                    <Stack spacing={2}>
                        {listQuestions.map((question, questionIndex) => (
                            <Box key={questionIndex}>
                                <Box
                                    sx={{
                                        backgroundColor: '#F7F7F6',
                                        display: 'grid',
                                        gridTemplateColumns: '1fr 1fr',
                                        padding: '24px',
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: '20px',
                                            fontWeight: '600',
                                        }}
                                    >
                                        Q{question.number}. {question.title}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            textAlign: 'right',
                                            fontSize: '20px',
                                            fontWeight: '600',
                                        }}
                                    >
                                        Thời gian trả lời:{' '}
                                        {answerApprove.answerTime[question.id]}{' '}
                                        giây
                                    </Typography>
                                </Box>
                                {answers.length > 0 && (
                                    <RenderAnswerDetail
                                        questionType={question.kind}
                                        answerOptions={question.answer_options}
                                        answer={answers.find(
                                            (answer_detail) =>
                                                answer_detail.question_id ===
                                                question.id
                                        )}
                                        questionIndex={questionIndex}
                                    />
                                )}
                            </Box>
                        ))}
                    </Stack>
                    <Stack>
                        <Stack
                            mt={2}
                            spacing={2}
                            direction="row"
                            justifyContent="center"
                        >
                            <Button
                                sx={{
                                    ...styles.btn,
                                    color: colors.main,
                                }}
                                variant="outlined"
                                onClick={() => onClose(false)}
                            >
                                Đóng
                            </Button>
                            {answerApprove.status !== 'Đã duyệt' && (
                                <>
                                    {answerApprove.status !== 'Từ chối' && (
                                        <Button
                                            sx={{
                                                ...styles.btn,
                                                color: colors.main,
                                            }}
                                            type="submit"
                                            variant="outlined"
                                            onClick={() => {
                                                setStatusToUpdate(
                                                    t(
                                                        'page.dataCollect.status.rejected'
                                                    )
                                                );
                                                setModalConfirmInfo({
                                                    title: 'Xác nhận từ chối phản hồi',
                                                    content: (
                                                        <Box>
                                                            <Typography>
                                                                Bạn có chắc chắn
                                                                muốn từ chối
                                                                phản hồi của{' '}
                                                                <b>
                                                                    &ldquo;
                                                                    {
                                                                        answerApprove.user_id
                                                                    }{' '}
                                                                    -{' '}
                                                                    {
                                                                        answerApprove.userName
                                                                    }
                                                                    &ldquo;
                                                                </b>{' '}
                                                                không?
                                                            </Typography>
                                                        </Box>
                                                    ),
                                                });
                                                setOpenModalConfirm(true);
                                            }}
                                            //     handleUpdateStatus(
                                            //         t('page.dataCollect.status.rejected')
                                            //     )
                                            // }
                                        >
                                            Từ chối
                                        </Button>
                                    )}
                                    <Button
                                        type="submit"
                                        sx={{
                                            ...styles.btn,
                                            color: (theme) =>
                                                theme.palette.common.white,
                                        }}
                                        variant="contained"
                                        onClick={
                                            () => {
                                                setStatusToUpdate(
                                                    t(
                                                        'page.dataCollect.status.approved'
                                                    )
                                                );
                                                setModalConfirmInfo({
                                                    title: 'Xác nhận phê duyệt phản hồi',
                                                    content: (
                                                        <Box>
                                                            <Typography>
                                                                Bạn có chắc chắn
                                                                muốn phê duyệt
                                                                phản hồi của{' '}
                                                                <b>
                                                                    &ldquo;
                                                                    {
                                                                        answerApprove.user_id
                                                                    }{' '}
                                                                    -{' '}
                                                                    {
                                                                        answerApprove.userName
                                                                    }
                                                                    &ldquo;
                                                                </b>{' '}
                                                                không?
                                                            </Typography>
                                                            <Typography>
                                                                <b>Lưu ý:</b>
                                                                Khi duyệt phản
                                                                hồi thì tất cả
                                                                dữ liệu đã duyệt
                                                                sẽ không thay
                                                                đổi được
                                                            </Typography>
                                                        </Box>
                                                    ),
                                                });
                                                setOpenModalConfirm(true);
                                            }
                                            // handleUpdateStatus(
                                            //     t('page.dataCollect.status.approved')
                                            // )
                                        }
                                    >
                                        Phê duyệt
                                    </Button>
                                </>
                            )}
                        </Stack>
                    </Stack>
                </FormProvider>
            )}
            <ModalConfirm
                open={openModalConfirm}
                onClose={() => setOpenModalConfirm(false)}
                onOk={handleUpdateStatus}
                textOk="Xác nhận"
                title={modalConfirmInfo.title}
                content={modalConfirmInfo.content}
            />
        </BaseDialog>
    );
};

const styles = createStyleSheet({
    btn: {
        textTransform: 'none',
        borderRadius: '40px',
        minWidth: '180px',
        minHeight: '60px',
        fontSize: '20px',
        fontWeight: '600',
    },
});

export default DialogAnswerModeration;
